import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { btn } from "@/components/ds/styles";
import { MarkLast } from "@/components/public/reading-page";
import { formatUsdShort } from "@/lib/payments/currency";
import { useTraumaPurchaseState } from "@/lib/trauma/purchase-query";
import { useSuspenseQuery } from "@tanstack/react-query";
import { RouteError, RouteNotFound } from "@/components/route-boundaries";
import { TraumaPreviewBanner } from "@/components/trauma/preview-banner";
import { TraumaAccessCta } from "@/components/trauma/access-cta";
import { loadTraumaCatalogOrRedirect, traumaCatalogQuery } from "@/lib/trauma/catalog-query";
import { traumaDescription } from "@/lib/trauma/public-gate";
import { isTraumaCourseId } from "@/lib/trauma/ids";
import { TRAUMA_UI, TRAUMA_BUY_UI as BUY } from "@/lib/trauma/ui-copy";
import { canonicalLink, canonicalMeta, ogImageMeta } from "@/lib/seo";

export const Route = createFileRoute("/_public/courses/trauma/$id")({
  loader: async ({ context, params }) => {
    const catalog = await loadTraumaCatalogOrRedirect(context.queryClient);
    const course = isTraumaCourseId(params.id) ? catalog.courses.find((c) => c.id === params.id) : undefined;
    if (!course) throw notFound();
    return { enabled: catalog.enabled, title: course.title, description: traumaDescription(course.definition, course.tldr) };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) return { meta: [{ title: "غير متاح" }, { name: "robots", content: "noindex" }] };
    const title = `${loaderData.title} — د. سارة النحّاس`;
    const path = `/courses/trauma/${params.id}`;
    return {
      meta: [
        { title },
        { name: "description", content: loaderData.description },
        { property: "og:title", content: title },
        { property: "og:description", content: loaderData.description },
        { property: "og:type", content: "website" },
        { property: "og:locale", content: "ar_SY" },
        ...(loaderData.enabled ? [] : [{ name: "robots", content: "noindex, nofollow" }]),
        ...ogImageMeta(),
        ...canonicalMeta(path),
      ],
      links: [canonicalLink(path)],
    };
  },
  errorComponent: RouteError,
  notFoundComponent: RouteNotFound,
  component: CoursePage,
});

function CoursePage() {
  const { id } = Route.useParams();
  const { data } = useSuspenseQuery(traumaCatalogQuery());
  const { gate, owned } = useTraumaPurchaseState();
  const course = data?.courses.find((c) => c.id === id);
  if (!data || !course) return null;
  const isOwned = owned.includes(course.id);
  const price = data.prices[`trauma-${course.id}`];
  return (
    <div className="bg-cream-200 pb-16">
      <article className="mx-auto max-w-3xl px-5 pt-10 text-[17px] leading-[1.85] text-text-primary lg:pt-16">
        <TraumaPreviewBanner show={!data.enabled} />
        <p className="font-display text-[14px] font-bold text-amber-800">الدورة {course.order} من 12</p>
        <h1 className="mt-3 font-display text-[34px] font-extrabold leading-[1.3] lg:text-[42px]"><MarkLast text={course.title} /></h1>
        <p className="mt-4 text-[19px] leading-[1.8] text-brown-500">{course.subtitle}</p>

        <p className="mt-8 font-bold">{course.definition}</p>
        <p className="mt-4">{course.tldr}</p>
        <p className="mt-4 whitespace-pre-line">{course.opening}</p>
      </article>

      {isOwned && !gate?.isAdmin ? (
        <section className="mx-3 mt-10 rounded-ds-2xl bg-green-100 px-6 py-9 lg:mx-auto lg:max-w-[76rem] lg:px-12">
          <div className="mx-auto max-w-3xl">
            <h2 className="font-display text-[23px] font-extrabold text-green-800">{BUY.ownedCourse}</h2>
            <Link to="/courses/trauma/$id/play" params={{ id: course.id }} className={`${btn.primary} mt-5`}>متابعة ←</Link>
          </div>
        </section>
      ) : null}

      <section className="mx-auto mt-12 max-w-3xl px-5">
        <h2 className="font-display text-[30px] font-extrabold text-text-primary">{TRAUMA_UI.discoverHeading}</h2>
        <p className="mt-2 text-[14px] text-brown-500">{TRAUMA_UI.facts}</p>
        <ol className="mt-5 divide-y divide-hairline-subtle">
          {course.axes.map((a) => (
            <li key={a.n} className="flex gap-4 py-5">
              <span dir="ltr" className="w-12 shrink-0 font-display text-[34px] font-extrabold leading-none text-amber-800">{String(a.n).padStart(2, "0")}</span>
              <div className="min-w-0">
                <p className="font-display text-[17px] font-bold text-text-primary">{a.label}</p>
                <p className="mt-1 text-[15px] leading-[1.8] text-brown-500">{a.question}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      {!(isOwned && !gate?.isAdmin) && (
        <section className="mx-auto mt-10 max-w-3xl px-5">
          <div className="rounded-ds-xl border border-hairline-subtle bg-surface p-6 shadow-sm">
            {price != null ? <p className="font-display text-[30px] font-extrabold leading-none text-amber-800"><span dir="ltr">{formatUsdShort(price)}</span></p> : null}
            <div className="mt-5"><TraumaAccessCta courseId={course.id} /></div>
            <p className="mt-4 text-[14px] text-brown-500">كل المبيعات نهائية.</p>
          </div>
        </section>
      )}
    </div>
  );
}
